import React, {
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { MeasureLabelsShort, Tender, TenderCategory } from '@app/models';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { CardFavoriteButton, CardHideActiveButton } from '@app/components';
import {
  getCompanyTypeShortName,
  getFormattedDate,
  shotName,
  hidePhoneNumbers,
} from '@app/helpers';
import { IconCalendar, IconMapMarker, IconWrench } from '@app/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from '@app/providers';
import {
  activateTender,
  addToFavorites,
  deleteFromFavorites,
  hideTender,
} from '@app/api';

interface Props {
  tender: Tender;
  horizontal?: boolean;
  onClick?: (id: string) => void;
  fallbackImage?: string;
  onChangeFavorite?: (id: string, isFavorite: boolean) => void;
  hideThumbnail?: boolean;
  tenderCategory?: TenderCategory;
  onChangeActive?: (id: string, isActive: boolean) => void;
}

function TenderCard(props: Props) {
  const {
    tender,
    horizontal,
    onClick,
    fallbackImage,
    onChangeFavorite,
    hideThumbnail,
    tenderCategory,
    onChangeActive,
  } = props;

  const { owner, oked, kato, company, active, isFavorite } = tender;
  const { user, showSignInModal } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isProfile = location.pathname.includes('profile');

  const onClickTender = useCallback(
    (id: string) => () => {
      if (!!onClick) {
        navigate(`/profile/tender/${id}`);
        return;
      }
      navigate(`/tender/${id}`);
    },
    [navigate, onClick]
  );

  const onClickAddFavorite = useCallback(
    async (isFavorite: boolean) => {
      if (!user.authenticated) {
        showSignInModal();
        return;
      }
      isFavorite
        ? await addToFavorites({ tenderId: tender.id })
        : await deleteFromFavorites({ tenderId: tender.id });
      if (!!onChangeFavorite) {
        onChangeFavorite(tender.id, isFavorite);
      }
    },
    [onChangeFavorite, showSignInModal, tender.id, user.authenticated]
  );

  const onClickHideActive = useCallback(
    async (newActiveState: boolean) => {
      if (!user.authenticated) return;
      try {
        if (newActiveState) {
          await activateTender(tender.id);
        } else {
          await hideTender(tender.id);
        }
        if (onChangeActive) {
          onChangeActive(tender.id, newActiveState);
        }
      } catch (e) {
        console.error('Failed to update tender status', e);
        throw e;
      }
    },
    [tender.id, user.authenticated, onChangeActive]
  );

  const actionButtons = useMemo<ReactNode[]>(() => {
    if (!tender) return [];
    const returnActionButtons = [
      <Button
        key="details"
        onClick={onClickTender(tender.id)}
        className="flex-1 py-2"
        variant="default"
        size="sm"
      >
        Подробнее
      </Button>,
      <CardFavoriteButton
        key="favorite"
        onClickFavorite={onClickAddFavorite}
        isFavorite={isFavorite}
      />,
    ];
    if (isProfile && tender.ownerId == user.id) {
      returnActionButtons.push(
        <CardHideActiveButton
          key="hideactive"
          onClickHideActive={onClickHideActive}
          isActive={active}
        />
      );
    }
    return [...returnActionButtons];
  }, [
    active,
    isFavorite,
    isProfile,
    onClickAddFavorite,
    onClickHideActive,
    tender,
    user.id,
  ]);

  const imageIds = useMemo(() => {
    return tender.imagesIds ? tender.imagesIds.split(',') : [];
  }, [tender.imagesIds]);

  const authorInfo = useMemo(() => {
    const safeCompany = (company as Partial<typeof company>) ?? {};
    const safeOwner = (owner as Partial<typeof owner>) ?? {};
    const author = !!company
      ? {
          name: `${getCompanyTypeShortName(safeCompany.type as any)} ${
            safeCompany.name ?? ''
          }`,
          image: safeCompany.avatarFilesIds
            ? safeCompany.avatarFilesIds.split(',')[0]
            : '',
        }
      : {
          name: 'Физическое лицо',
          image: safeOwner.avatarFilesIds
            ? safeOwner.avatarFilesIds.split(',')[0]
            : '',
        };
    return {
      ...author,
      owner: shotName(safeOwner as any),
      type: safeOwner.type,
    };
  }, [company, owner]);

  const infos = useMemo(
    () => [
      { icon: IconWrench, text: oked ? oked.nameRu : 'Не указан' },
      { icon: IconMapMarker, text: kato.nameRu },
      {
        icon: IconCalendar,
        text:
          tenderCategory === TenderCategory.WORKER
            ? `Актуально до: ${getFormattedDate(tender.deliveryDate)}`
            : `Срок поставки: ${getFormattedDate(tender.deliveryDate)}`,
      },
    ],
    [kato.nameRu, oked, tender.deliveryDate, tenderCategory]
  );

  const [mainImageSrc, setMainImageSrc] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (imageIds.length > 0) {
      const firstImageId = imageIds[0];
      import('@app/api').then(({ getFileById }) => {
        getFileById(firstImageId)
          .then((blob) => {
            if (!isMounted) return;
            const url = URL.createObjectURL(blob);
            setMainImageSrc(url);
          })
          .catch(() => {
            if (!isMounted) return;
            setMainImageSrc(fallbackImage || null);
          });
      });
    } else {
      setMainImageSrc(fallbackImage || null);
    }

    return () => {
      isMounted = false;
      if (mainImageSrc && !mainImageSrc.startsWith('http')) {
        URL.revokeObjectURL(mainImageSrc);
      }
    };
  }, [imageIds, fallbackImage]);

  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  useEffect(() => {
    if (!tender.endDate) return;
    const update = () => {
      const now = new Date();
      const end = new Date(tender.endDate);
      const diff = end.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft('Завершен');
        return;
      }
      if (diff < 24 * 60 * 60 * 1000) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft(
          `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      } else {
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        setTimeLeft(`Ост: ${days} дней`);
      }
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [tender.endDate]);

  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  useEffect(() => {
    let isMounted = true;
    const fileId = authorInfo.image;
    if (fileId) {
      import('@app/api').then(({ getFileById }) => {
        getFileById(fileId)
          .then((blob) => {
            if (!isMounted) return;
            const url = URL.createObjectURL(blob);
            setAvatarSrc(url);
          })
          .catch(() => setAvatarSrc(null));
      });
    } else {
      setAvatarSrc(null);
    }
    return () => {
      isMounted = false;
    };
  }, [authorInfo.image]);

  return (
    <Card
      className={`
        bg-white border rounded-xl shadow-sm hover:shadow-2xl hover:-translate-y-2 hover:bg-blue-50 
        transition-all w-full cursor-pointer overflow-hidden
        ${
          horizontal
            ? 'flex flex-col sm:flex-row items-stretch'
            : 'flex flex-col'
        }
      `}
      onClick={onClickTender(tender.id)}
    >
      {!hideThumbnail && (
        <div
          className={`
            relative flex-shrink-0 hidden sm:block
            ${
              horizontal
                ? 'w-full h-48 sm:w-48 sm:h-auto'
                : 'w-full h-48 sm:h-56 md:h-64'
            }
          `}
        >
          {mainImageSrc ? (
            <img
              src={mainImageSrc}
              alt={tender.name}
              className={`
                object-cover w-full h-full
                ${
                  horizontal
                    ? 'rounded-t-xl sm:rounded-t-none sm:rounded-l-xl'
                    : 'rounded-t-xl'
                }
              `}
            />
          ) : (
            <div
              className={`
                bg-gray-200 flex items-center justify-center w-full h-full
                ${
                  horizontal
                    ? 'rounded-t-xl sm:rounded-t-none sm:rounded-l-xl'
                    : 'rounded-t-xl'
                }
              `}
            >
              <span className="text-gray-400 text-sm">Нет изображения</span>
            </div>
          )}
        </div>
      )}

      <div
        className={`
          flex-1 flex min-w-0
          ${horizontal ? 'flex-col sm:flex-row' : 'flex-col'}
        `}
      >
        <div className="flex-1 min-w-0 p-3 sm:p-4">
          <div className="mb-2">
            <h3 className="text-sm sm:text-base font-semibold line-clamp-2 mb-1 break-words">
              {tender.name}
            </h3>
          </div>

          <div className="space-y-1 text-xs text-gray-500 mb-2">
            {infos.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <div key={index} className="flex items-start gap-1 min-w-0">
                  <IconComponent className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span className="break-words leading-tight min-w-0">
                    {info.text}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mb-3">
            <p className="text-xs sm:text-sm text-gray-700 line-clamp-2 break-words leading-relaxed">
              {user.authenticated
                ? tender.description
                : hidePhoneNumbers(tender.description)}
            </p>
          </div>

          <div className="flex items-center gap-2 min-w-0">
            {avatarSrc && (
              <Avatar className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0">
                <AvatarImage
                  src={avatarSrc}
                  alt={authorInfo.name}
                  className="object-cover"
                />
              </Avatar>
            )}
            <div className="text-xs sm:text-sm min-w-0">
              <div className="font-medium truncate">{authorInfo.name}</div>
              <div className="text-gray-500 truncate">{authorInfo.owner}</div>
            </div>
          </div>
        </div>

        <div
          className={`
            flex flex-col justify-between border-t sm:border-t-0 p-3 sm:p-4
            ${
              horizontal
                ? 'sm:border-l sm:min-w-[160px] sm:max-w-[200px]'
                : 'border-t'
            }
          `}
        >
          <div className="flex flex-row justify-between space-y-2 mb-3">
            {timeLeft && (
              <div className="flex justify-end">
                <Badge
                  className={`
                    px-2 py-1 rounded-sm font-semibold text-xs shadow
                    ${
                      timeLeft === 'Завершен'
                        ? 'bg-red-500 text-white'
                        : 'bg-green-600 text-white'
                    }
                  `}
                >
                  {timeLeft}
                </Badge>
              </div>
            )}

            <div className="text-right">
              <div className="text-lg sm:text-xl font-bold text-blue-700 break-words">
                {tender.price}₸
                {tender.measure && (
                  <span className="text-xs sm:text-sm font-normal text-gray-500 ml-0.5">
                    /{MeasureLabelsShort[tender.measure]}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-row justify-between gap-1 mb-3">
            {/* flex gap-2 flex gap-2 flex-row flex-wrap*/}
            {actionButtons}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default memo(TenderCard);
